import { PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { Link } from "react-router-dom";
import { Fragment, type MouseEvent, useEffect, useRef, useState } from "react";
import { type CompanyContext, type UserProfile } from "@/shared/api/api-client";
import { hasRoleAtLeast } from "@/shared/utils/roles";
import { carriedSearch, navigationGroups, navigationItems, type NavItem } from "@/shell/navigation";
import { BrandWordmark } from "@/shell/BrandWordmark";
import { AccountMenu } from "@/shell/AccountMenu";
import { formatBadgeCount, formatInteger } from "@/shared/utils/format";

type SidebarProps = {
  pathname: string;
  /** A query da URL atual: o menu carrega só o `?upload=` entre menções e justificativas. */
  search: string;
  company: CompanyContext | null;
  open: boolean;
  close: () => void;
  expanded: boolean;
  toggleExpanded: () => void;
  profile: UserProfile;
  mentionCount: number;
  logout: () => void;
};

export function Sidebar({
  pathname,
  search,
  company,
  open,
  close,
  expanded,
  toggleExpanded,
  profile,
  mentionCount,
  logout
}: SidebarProps) {
  const hasCompanyContext = Boolean(company);
  const [tooltipSuppressed, setTooltipSuppressed] = useState(false);
  const [companyNavMounted, setCompanyNavMounted] = useState(hasCompanyContext);
  const [companyNavVisible, setCompanyNavVisible] = useState(hasCompanyContext);
  const suppressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    let frame: number | undefined;
    let unmountTimer: ReturnType<typeof setTimeout> | undefined;
    if (hasCompanyContext) {
      setCompanyNavMounted(true);
      frame = window.requestAnimationFrame(() => setCompanyNavVisible(true));
    } else {
      setCompanyNavVisible(false);
      unmountTimer = setTimeout(() => setCompanyNavMounted(false), 240);
    }
    return () => {
      if (frame !== undefined) window.cancelAnimationFrame(frame);
      if (unmountTimer) clearTimeout(unmountTimer);
    };
  }, [hasCompanyContext]);

  const handleToggleExpanded = (event: MouseEvent<HTMLButtonElement>) => {
    // O botão colapsado/expandido reaproveita o mesmo nó do DOM entre os dois
    // estados (mesma posição na árvore), então o foco do clique persiste e
    // deixa o ícone "travado" visível via :focus-within. Soltar o foco aqui
    // evita esse resíduo.
    event.currentTarget.blur();
    setTooltipSuppressed(true);
    if (suppressTimerRef.current) clearTimeout(suppressTimerRef.current);
    suppressTimerRef.current = setTimeout(() => setTooltipSuppressed(false), 700);
    toggleExpanded();
  };

  const renderItem = ({ label, icon: Icon, href, renderAfter }: NavItem) => {
    const target = href(company?.id ?? undefined);
    const active = pathname === target;
    const link = (
      <Link
        key={label}
        to={target + carriedSearch(pathname, search, target)}
        data-tooltip={label}
        aria-label={label}
        aria-current={active ? "page" : undefined}
        className={active ? "active" : ""}
        onClick={close}
      >
        <Icon size={18}/>
        <span>{label}</span>
        {label === "Análises e menções" && mentionCount > 0 && (
          <em title={`${formatInteger(mentionCount)} menções`}>{formatBadgeCount(mentionCount)}</em>
        )}
      </Link>
    );
    if (!renderAfter) return link;
    return (
      <Fragment key={label}>
        {link}
        {renderAfter({ companyId: company?.id ?? undefined, pathname, expanded, close })}
      </Fragment>
    );
  };

  return (
    <>
      {open && <button className="sidebar-scrim" onClick={close} aria-label="Fechar menu"/>}
      <aside className={`sidebar ${open ? "sidebar-open" : ""} ${expanded ? "sidebar-expanded" : ""}`}>
        <div className="brand">
          {!expanded ? (
            <div className="brand-control">
              <Link to="/" aria-label="Ir para a home" onClick={close}>
                <div className="brand-symbol" aria-hidden="true">
                  <span className="brand-symbol-icon" role="img" aria-label="Reputaition" />
                </div>
              </Link>
              <button
                type="button"
                className="sidebar-toggle-collapsed"
                data-tooltip={tooltipSuppressed ? undefined : "Expandir menu"}
                aria-label="Expandir menu"
                onClick={handleToggleExpanded}
              >
                <PanelLeftOpen size={16}/>
              </button>
            </div>
          ) : (
            <div className="brand-expanded-container">
              <Link to="/" className="brand-wordmark" aria-label="Ir para a home" onClick={close}>
                <BrandWordmark />
              </Link>
              <button
                type="button"
                className="sidebar-toggle-expanded"
                aria-label="Recolher menu"
                onClick={handleToggleExpanded}
              >
                <PanelLeftClose size={18}/>
              </button>
            </div>
          )}
        </div>
        <nav aria-label="Navegação principal">
          {navigationItems
            .filter(item => item.scope === "global" && item.placement !== "account")
            .filter(item => !item.requiresRole || hasRoleAtLeast(profile, item.requiresRole))
            .map(renderItem)}
          {companyNavMounted && (
            <div
              className={`sidebar-company-nav ${companyNavVisible ? "is-visible" : ""}`}
              aria-hidden={!companyNavVisible}
            >
              {/* O filho existe pra animação por grid — ver `.sidebar-company-nav` em shell.css. */}
              <div className="sidebar-company-nav-body">
                <div className="sidebar-company-divider" aria-hidden="true"/>
                {navigationGroups.map(group => {
                  const items = navigationItems
                    .filter(item => item.group === group.key)
                    // O título do grupo ("RECORTES") some junto: o
                    // return null logo abaixo já cuida do grupo vazio.
                    .filter(item => !item.requiresCena || (company?.displays_cena ?? true))
                    // Mesma escada dos itens globais: "Reprocessamentos" é de
                    // Administrador ou acima, como a API que ele lê.
                    .filter(item => !item.requiresRole || hasRoleAtLeast(profile, item.requiresRole));
                  if (!items.length) return null;
                  return (
                    <Fragment key={group.key}>
                      <p>{group.title}</p>
                      {items.map(renderItem)}
                    </Fragment>
                  );
                })}
              </div>
            </div>
          )}
        </nav>
        <div className="sidebar-foot">
          <AccountMenu profile={profile} pathname={pathname} logout={logout} variant="sidebar"/>
        </div>
      </aside>
    </>
  );
}
